// OpportunityPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Table,
  InputGroup,
  Badge,
  Stack,
} from 'react-bootstrap';
import OpportunityHeader from './OpportunityHeader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { add_product_to_opp, check_task, load_opportunity, load_opportunity_log, load_opportunity_products, load_products, remove_product_to_opp, send_newLogEntry, update_opp_product_pvp } from '../../../utils/crm_services';
import { create_new_task, get_all_members } from '../../../utils/talent_services';
import Select from 'react-select';
import Modal_proyectos from '../../menus/Modal';
import UserAvatar from '../dashboard/UserAvatar';
import * as XLSX from 'xlsx-js-style';

const formatCurrency = (value) =>
  (Number(value) || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const EXCEL_HEADER_STYLE = {
  font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
  fill: { patternType: 'solid', fgColor: { rgb: '1F4E79' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: '1F4E79' } },
    bottom: { style: 'thin', color: { rgb: '1F4E79' } },
    left: { style: 'thin', color: { rgb: '1F4E79' } },
    right: { style: 'thin', color: { rgb: '1F4E79' } },
  },
};

const EXCEL_CELL_BORDER = {
  top: { style: 'thin', color: { rgb: 'D0D7DE' } },
  bottom: { style: 'thin', color: { rgb: 'D0D7DE' } },
  left: { style: 'thin', color: { rgb: 'D0D7DE' } },
  right: { style: 'thin', color: { rgb: 'D0D7DE' } },
};

const EXCEL_TOTAL_STYLE = {
  font: { bold: true, color: { rgb: '0F5132' }, sz: 11 },
  fill: { patternType: 'solid', fgColor: { rgb: 'D1E7DD' } },
  alignment: { vertical: 'center' },
  border: {
    top: { style: 'medium', color: { rgb: '198754' } },
    bottom: { style: 'medium', color: { rgb: '198754' } },
    left: { style: 'thin', color: { rgb: '198754' } },
    right: { style: 'thin', color: { rgb: '198754' } },
  },
};

const EXCEL_MONEY_FORMAT = '"$"#,##0.00';
const EXCEL_NUMBER_FORMAT = '#,##0';

function downloadOpportunityProductsExcel({
  filename,
  sheetName,
  isInternalUser,
  products,
}) {
  const headers = isInternalUser
    ? ['Product', 'Units', 'EXW', 'Total EXW', 'PVP', 'Total PVP']
    : ['Product', 'Units', 'PVP', 'Total PVP'];

  const colWidths = isInternalUser
    ? [{ wch: 42 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 }]
    : [{ wch: 42 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];

  const moneyCols = isInternalUser ? [2, 3, 4, 5] : [2, 3];
  const totalExwCol = isInternalUser ? 'D' : null;
  const totalPvpCol = isInternalUser ? 'F' : 'D';
  const unitsCol = 'B';
  const exwCol = 'C';
  const pvpCol = isInternalUser ? 'E' : 'C';

  const aoa = [headers];

  products.forEach((item) => {
    const units = parseFloat(item.units) || 0;
    const exwPrice = parseFloat(item.exw_price) || 0;
    const pvp = parseFloat(item.pvp) || 0;

    if (isInternalUser) {
      aoa.push([item.name || '', units, exwPrice, null, pvp, null]);
    } else {
      aoa.push([item.name || '', units, pvp, null]);
    }
  });

  const dataStart = 2;
  const dataEnd = products.length + 1;
  const totalRowIndex = products.length + 2;
  aoa.push(isInternalUser ? ['TOTAL', null, null, null, null, null] : ['TOTAL', null, null, null]);

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet['!cols'] = colWidths;
  worksheet['!rows'] = [{ hpt: 22 }];

  // Header styles
  headers.forEach((_, colIdx) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c: colIdx });
    const cell = worksheet[ref] || { t: 's', v: '' };
    cell.s = EXCEL_HEADER_STYLE;
    worksheet[ref] = cell;
  });

  // Data rows + line formulas
  for (let row = dataStart; row <= dataEnd; row++) {
    const productRef = XLSX.utils.encode_cell({ r: row - 1, c: 0 });
    const unitsRef = XLSX.utils.encode_cell({ r: row - 1, c: 1 });

    worksheet[productRef] = {
      ...(worksheet[productRef] || { t: 's', v: '' }),
      s: {
        alignment: { horizontal: 'left', vertical: 'center' },
        border: EXCEL_CELL_BORDER,
        fill: {
          patternType: 'solid',
          fgColor: { rgb: row % 2 === 0 ? 'F8F9FA' : 'FFFFFF' },
        },
      },
    };

    worksheet[unitsRef] = {
      ...(worksheet[unitsRef] || { t: 'n', v: 0 }),
      t: 'n',
      z: EXCEL_NUMBER_FORMAT,
      s: {
        alignment: { horizontal: 'center', vertical: 'center' },
        border: EXCEL_CELL_BORDER,
        fill: {
          patternType: 'solid',
          fgColor: { rgb: row % 2 === 0 ? 'F8F9FA' : 'FFFFFF' },
        },
      },
    };

    if (isInternalUser) {
      const exwRef = XLSX.utils.encode_cell({ r: row - 1, c: 2 });
      const totalExwRef = XLSX.utils.encode_cell({ r: row - 1, c: 3 });
      const pvpRef = XLSX.utils.encode_cell({ r: row - 1, c: 4 });
      const totalPvpRef = XLSX.utils.encode_cell({ r: row - 1, c: 5 });
      const rowFill = {
        patternType: 'solid',
        fgColor: { rgb: row % 2 === 0 ? 'F8F9FA' : 'FFFFFF' },
      };

      worksheet[exwRef] = {
        ...(worksheet[exwRef] || { t: 'n', v: 0 }),
        t: 'n',
        z: EXCEL_MONEY_FORMAT,
        s: { alignment: { horizontal: 'right', vertical: 'center' }, border: EXCEL_CELL_BORDER, fill: rowFill },
      };
      worksheet[totalExwRef] = {
        t: 'n',
        f: `${unitsCol}${row}*${exwCol}${row}`,
        z: EXCEL_MONEY_FORMAT,
        s: { alignment: { horizontal: 'right', vertical: 'center' }, border: EXCEL_CELL_BORDER, fill: rowFill },
      };
      worksheet[pvpRef] = {
        ...(worksheet[pvpRef] || { t: 'n', v: 0 }),
        t: 'n',
        z: EXCEL_MONEY_FORMAT,
        s: { alignment: { horizontal: 'right', vertical: 'center' }, border: EXCEL_CELL_BORDER, fill: rowFill },
      };
      worksheet[totalPvpRef] = {
        t: 'n',
        f: `${unitsCol}${row}*${pvpCol}${row}`,
        z: EXCEL_MONEY_FORMAT,
        s: { alignment: { horizontal: 'right', vertical: 'center' }, border: EXCEL_CELL_BORDER, fill: rowFill },
      };
    } else {
      const pvpRef = XLSX.utils.encode_cell({ r: row - 1, c: 2 });
      const totalPvpRef = XLSX.utils.encode_cell({ r: row - 1, c: 3 });
      const rowFill = {
        patternType: 'solid',
        fgColor: { rgb: row % 2 === 0 ? 'F8F9FA' : 'FFFFFF' },
      };

      worksheet[pvpRef] = {
        ...(worksheet[pvpRef] || { t: 'n', v: 0 }),
        t: 'n',
        z: EXCEL_MONEY_FORMAT,
        s: { alignment: { horizontal: 'right', vertical: 'center' }, border: EXCEL_CELL_BORDER, fill: rowFill },
      };
      worksheet[totalPvpRef] = {
        t: 'n',
        f: `${unitsCol}${row}*${pvpCol}${row}`,
        z: EXCEL_MONEY_FORMAT,
        s: { alignment: { horizontal: 'right', vertical: 'center' }, border: EXCEL_CELL_BORDER, fill: rowFill },
      };
    }
  }

  // Totals row with SUM formulas
  const totalLabelRef = XLSX.utils.encode_cell({ r: totalRowIndex - 1, c: 0 });
  worksheet[totalLabelRef] = {
    t: 's',
    v: 'TOTAL',
    s: { ...EXCEL_TOTAL_STYLE, alignment: { horizontal: 'left', vertical: 'center' } },
  };

  for (let c = 1; c < headers.length; c++) {
    const ref = XLSX.utils.encode_cell({ r: totalRowIndex - 1, c });
    const isMoney = moneyCols.includes(c);
    const isTotalExw = isInternalUser && c === 3;
    const isTotalPvp = (isInternalUser && c === 5) || (!isInternalUser && c === 3);

    if (isTotalExw) {
      worksheet[ref] = {
        t: 'n',
        f: `SUM(${totalExwCol}${dataStart}:${totalExwCol}${dataEnd})`,
        z: EXCEL_MONEY_FORMAT,
        s: { ...EXCEL_TOTAL_STYLE, alignment: { horizontal: 'right', vertical: 'center' } },
      };
    } else if (isTotalPvp) {
      worksheet[ref] = {
        t: 'n',
        f: `SUM(${totalPvpCol}${dataStart}:${totalPvpCol}${dataEnd})`,
        z: EXCEL_MONEY_FORMAT,
        s: { ...EXCEL_TOTAL_STYLE, alignment: { horizontal: 'right', vertical: 'center' } },
      };
    } else {
      worksheet[ref] = {
        t: 's',
        v: '',
        s: {
          ...EXCEL_TOTAL_STYLE,
          alignment: { horizontal: isMoney ? 'right' : 'center', vertical: 'center' },
        },
      };
    }
  }

  if (products.length === 0) {
    // Keep sheet valid even without rows
    worksheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: 0, c: headers.length - 1 },
    });
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || 'Productos');
  XLSX.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

export default function OpportunityPage({usr_type, usr_id, cards = []}) {
  const isInternalUser = parseInt(usr_type, 10) === 0;
  const token = localStorage.getItem("token");

  // Estado del header
  const [status, setStatus] = useState('Draft');
 const navigate = useNavigate();
  // Formulario
  const [contactName, setContactName] = useState('');
  const [newUnits, setNewUnits] = useState('');
  const [newPvp, setNewPvp] = useState('');
  const [selectedNewProduct, setSelectedNewProduct] = useState('');
  const [opportunitySize, setOpportunitySize] = useState(0);
  const [opportunityPvpSize, setOpportunityPvpSize] = useState(0);
  const [productOptions, setproductOptions] = useState([]);
  const [productList, setproductList] = useState([]);

  const [newOpportunity, setNewOpportunity] = useState(false);

  const [searchParams] = useSearchParams();
  const opportunity_id = searchParams.get("id");
  const originalChannel_id = searchParams.get("channel_id");
 
    // --- Estados adicionales ---
  // Estado para saber si el log es público (true) o privado (false)
  const [isPrivate, setIsPrivate] = useState(false); 
  // Estado para saber si es una tarea
  const [isTask, setIsTask] = useState(false);
  // Estado para la fecha de vencimiento
  const [expiryDate, setExpiryDate] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [newTaskAssignUsrId, setNewTaskAssignUsrId] = useState(() => (usr_id != null ? Number(usr_id) : null));
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [assigneeOptions, setAssigneeOptions] = useState([]);

  const getProductByValue = (value) => {
    // Si el valor es null o undefined, retorna null para limpiar el Select
    if (value === null || value === undefined) {
      return null;
    }
    // Busca la opción correspondiente en productOptions
    return productOptions.find(option => option.value === value) || null;
  };
  


  //LOADING
    useEffect(() => {
      if (opportunity_id !== null) {
        if(opportunity_id==="new"){
            setNewOpportunity(true);
        }else{
            setNewOpportunity(false);
        }
      }
    }, [opportunity_id]); 

   

  async function load_log() {
    const opp_log = await load_opportunity_log(token,opportunity_id);
    if (opp_log==="INVALID"){
        navigate("/");
    }else{

     const templog= opp_log.map((celement, index) => {
       return {
           id: celement.id,
           issue_date: celement.issue_date,
           description: celement.description,
           is_private: celement.is_private,
           type_name: celement.type_name,
           issue_type: celement.type,
           expiryDate: celement.expiry_date,
           user_by: celement.usr_name,
           done: parseInt(celement.done)==1?true:false,
           index:index
       };
     });

      setLog( templog);
        
    }
}
  
  async function fetch_opportunity_products() {
           const opp_product_list = await load_opportunity_products(token,opportunity_id);
           if (opp_product_list==="INVALID"){
               navigate("/");
           }else{

            let exwTotal = 0;
            let pvpTotal = 0;
            const tempproducts= opp_product_list.map((celement, index) => {
              const units = parseFloat(celement.units) || 0;
              const exwPrice = parseFloat(celement.exw_price) || 0;
              const pvp = parseFloat(celement.pvp) || 0;
              if (isInternalUser) {
                exwTotal += units * exwPrice;
              }
              pvpTotal += units * pvp;
              return {
                  id: celement.id,
                  units: celement.units,
                  name: celement.name,
                  description: celement.prod_description,
                  exw_price: celement.exw_price,
                  pvp,
                  index:index
              };
            });
  
             setproductList( tempproducts);
             setOpportunitySize(exwTotal);
             setOpportunityPvpSize(pvpTotal);
               
           }
  }
  

  const addItem = () =>{
    if (!selectedNewProduct || !newUnits) return;

     async function fetchAddProduct() {
            const cardList = await add_product_to_opp(
              token,
              opportunity_id,
              selectedNewProduct,
              newUnits,
              newPvp || 0
            );
            if (cardList==="INVALID"){
                navigate("/");
            }else{
              setSelectedNewProduct(null);
              setNewUnits('');
              setNewPvp('');
              fetch_opportunity_products();
            }
        }
        fetchAddProduct();
  }

  
  async function fetchRemoveItem(id) {
    const cardList = await remove_product_to_opp(token,id);
    if (cardList==="INVALID"){
        navigate("/");
    }else{
      fetch_opportunity_products();
    }
}

  const handlePvpChange = (id, value) => {
    setproductList((prev) =>
      prev.map((item) => (item.id === id ? { ...item, pvp: value } : item))
    );
  };

  const savePvp = async (id, value) => {
    const pvpValue = parseFloat(value) || 0;
    const result = await update_opp_product_pvp(token, id, pvpValue);
    if (result === "INVALID") {
      navigate("/");
      return;
    }
    setproductList((prev) => {
      const next = prev.map((item) =>
        item.id === id ? { ...item, pvp: pvpValue } : item
      );
      const pvpTotal = next.reduce(
        (sum, item) => sum + (parseFloat(item.units) || 0) * (parseFloat(item.pvp) || 0),
        0
      );
      setOpportunityPvpSize(pvpTotal);
      return next;
    });
  };

  const downloadProductsExcel = () => {
    if (!productList.length) return;
    const safeName = (opportunityName || `opportunity_${opportunity_id || 'export'}`)
      .replace(/[\\/:*?"<>|]/g, '_')
      .trim();
    downloadOpportunityProductsExcel({
      filename: `${safeName}_productos.xlsx`,
      sheetName: 'Productos',
      isInternalUser,
      products: productList,
    });
  };



  // Log
  const [logEntry, setLogEntry] = useState('');
  const [log, setLog] = useState([]);
  const [opportunityName, setOpportunityName] = useState('');

  const sendLog = () => {
    if (!logEntry.trim()) return;
    if (isInternalUser && isTask && !taskTitle.trim()) return;

    sendNewLogEntry();
    setLogEntry('');
  };

  const defaultAssigneeUsrId = usr_id != null && !Number.isNaN(Number(usr_id)) ? Number(usr_id) : null;

  const selectedAssignee = useMemo(
    () => assigneeOptions.find((opt) => opt.usr_id === newTaskAssignUsrId) || null,
    [assigneeOptions, newTaskAssignUsrId]
  );

  const filteredAssigneeOptions = useMemo(() => {
    const term = assigneeSearch.trim().toLowerCase();
    if (!term) return assigneeOptions;
    return assigneeOptions.filter((opt) => {
      const name = (opt.displayName || '').toLowerCase();
      const email = (opt.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [assigneeOptions, assigneeSearch]);

  const resetTaskControls = () => {
    setIsTask(false);
    setTaskTitle('');
    setExpiryDate('');
    setAssigneeSearch('');
    setAssigneePickerOpen(false);
    setNewTaskAssignUsrId(defaultAssigneeUsrId);
  };

  const handleTaskToggle = (checked) => {
    setIsTask(checked);
    if (!checked) {
      setTaskTitle('');
      setExpiryDate('');
      setAssigneeSearch('');
      setAssigneePickerOpen(false);
      setNewTaskAssignUsrId(defaultAssigneeUsrId);
    }
  };

  useEffect(() => {
    setNewTaskAssignUsrId(defaultAssigneeUsrId);
  }, [defaultAssigneeUsrId]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      const list = await get_all_members(token);
      if (cancelled) return;
      if (list === 'INVALID') {
        navigate('/');
        return;
      }
      if (!Array.isArray(list)) {
        setAssigneeOptions([]);
        return;
      }
      const normalized = list
        .map((row) => {
          const rawId = row.usr_id;
          const uid = rawId != null && rawId !== '' ? Number(rawId) : null;
          const name = (row.usr_name || '').trim();
          const email = (row.email || '').trim();
          const displayName = name || email || `Usuario ${uid ?? ''}`.trim();
          return { usr_id: uid, displayName, email };
        })
        .filter((x) => x.usr_id != null && !Number.isNaN(x.usr_id));

      if (defaultAssigneeUsrId != null && !normalized.some((x) => x.usr_id === defaultAssigneeUsrId)) {
        normalized.unshift({ usr_id: defaultAssigneeUsrId, displayName: 'Tú', email: '' });
      }

      const deduped = [];
      const seen = new Set();
      normalized.forEach((row) => {
        if (seen.has(row.usr_id)) return;
        seen.add(row.usr_id);
        deduped.push(row);
      });

      setAssigneeOptions(deduped);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, defaultAssigneeUsrId]);

  

   useEffect(() => {

    if (opportunity_id !== null && token != null) {
      if(opportunity_id==="new"){
          setNewOpportunity(true);
          setOpportunityName('');
      }else{
        setNewOpportunity(false);
        fetchLoadProducts();
        fetch_opportunity_products();
        load_log();
        load_opportunity(token, opportunity_id).then((r) => {
          if (r && r.opp_data && r.opp_data[0] && r.opp_data[0].name) {
            setOpportunityName(r.opp_data[0].name);
          } else {
            setOpportunityName('');
          }
        }).catch(() => setOpportunityName(''));
      }
    }

  }, [token, opportunity_id]);
  

    
  async function sendNewLogEntry() {
    const taskMode = isInternalUser && isTask;
    const productList = await send_newLogEntry(token, opportunity_id, logEntry, taskMode ? 3 : 2, isPrivate, expiryDate);
    if (productList === "INVALID") {
      navigate("/");
      return;
    }
    if (taskMode && taskTitle.trim() !== '') {
      const assignId = newTaskAssignUsrId != null && !Number.isNaN(Number(newTaskAssignUsrId))
        ? Number(newTaskAssignUsrId)
        : defaultAssigneeUsrId;
      const taskName = taskTitle.trim();
      const createResult = await create_new_task(token, taskName, {
        task_description: logEntry,
        opportunity_id: opportunity_id,
        due_date: expiryDate && expiryDate.trim() !== '' ? expiryDate : undefined,
        assignee_usr_id: assignId,
      });
      if (createResult === "INVALID") {
        navigate("/");
        return;
      }
    }
    load_log();
    resetTaskControls();
  }


  async function fetchLoadProducts() {
          const productList = await load_products(token);
          if (productList==="INVALID"){
              navigate("/");
          }else{
    
             const tempproducts= productList.map((celement, index) => {
              return {
                  value: celement.id,
                  label: celement.name,
                  description: celement.description,
                  exw_price: celement.exw_price,
                  index:index
              };
            });
  
             setproductOptions( tempproducts);
  
          }
      }

  async function handleTaskCompletion  (taskId, newStatus) {
         const opp_log = await check_task(token,taskId, newStatus);
    if (opp_log==="INVALID"){
        navigate("/");

    }else{
      load_log();
    }
  }
  

  return (
    <Container fluid className="py-4">
      {/* HEADER */}
      <Row className="mb-4">
        <Col>
        {token!="" &&
          <OpportunityHeader
            originalChannel_id={originalChannel_id}
            status={status}
            onStatusChange={setStatus}
            amount={80000}
            token={token}
            usr_type={usr_type}
            newOpportunity={newOpportunity}
            opportunity_id = {opportunity_id}
            usr_id={usr_id}
            cards={cards}
          />
        }
        </Col>
      </Row>

     {!newOpportunity && 
     <div>
      {/* FORMULARIO */}
      <Row className="gy-4 mb-4">
        {/* Fila 1: Contact Name | Phone | Protected until */}
        

       
      </Row>

      <Row className="mb-3 align-items-center">
        <Col xs="auto" className="me-auto">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={downloadProductsExcel}
            disabled={!productList.length}
          >
            Descargar Excel
          </Button>
        </Col>
        <Col className="text-end">
          {isInternalUser && (
            <h4 className="mb-1">
              Opportunity Amount (EXW):{' '}
              <span className="text-success fw-bold">
                {formatCurrency(opportunitySize)}
              </span>
            </h4>
          )}
          <h4 className="mb-0">
            Opportunity Amount (PVP):{' '}
            <span className="text-success fw-bold">
              {formatCurrency(opportunityPvpSize)}
            </span>
          </h4>
        </Col>
      </Row>
      {/* TABLA DE PRODUCTOS */}
      <Row className="mb-4">
        <Col>
       
          <div className="table-wrapper">
            <Table bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: isInternalUser ? '30%' : '45%' }}>Product</th>
                  <th style={{ width: '10%' }}>Units</th>
                  {isInternalUser && (
                    <>
                      <th style={{ width: '12%' }}>EXW</th>
                      <th style={{ width: '12%' }}>Total EXW</th>
                    </>
                  )}
                  <th style={{ width: '12%' }}>PVP</th>
                  <th style={{ width: '5%' }}></th>
                </tr>
              </thead>
              <tbody>
                {productList.map((it) => {
                  const units = parseFloat(it.units) || 0;
                  const exwPrice = parseFloat(it.exw_price) || 0;
                  return (
                  <tr key={it.id}>
                    <td>
                      {it.name}
                    </td>
                    <td>
                      {it.units}
                    </td>
                    {isInternalUser && (
                      <>
                        <td>{formatCurrency(exwPrice)}</td>
                        <td>{formatCurrency(units * exwPrice)}</td>
                      </>
                    )}
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        step="any"
                        value={it.pvp ?? ''}
                        onChange={(e) => handlePvpChange(it.id, e.target.value)}
                        onBlur={(e) => savePvp(it.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                      />
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => fetchRemoveItem(it.id)}
                      >
                        &minus;
                      </Button>
                    </td>
                  </tr>
                  );
                })}
                <tr>
                  <td >
                  <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            options={productOptions}
                            isLoading={productOptions.length === 0}
                            onChange={(selectedOption) =>{
                              setSelectedNewProduct(selectedOption.value);
                                }}
                            value={getProductByValue(selectedNewProduct)}
                            placeholder="Producto..."
                            isSearchable
                            menuPortalTarget={document.body}
                            styles={{
                                container: (provided) => ({
                                  ...provided,
                                  minWidth: 100, // o usa width: '100%' para expansión completa
                                  flex: 1,       // permite que crezca en d-flex
                                }),
                                  menu: (provided) => ({
                                      ...provided,
                                      zIndex: 9999 // Un valor alto para asegurar que aparezca encima de todo
                                  }),
                                  
                              }}
                        />
                  </td>
                  <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={newUnits}
                        onChange={(e) => setNewUnits( e.target.value)}
                      />
                    </td>
                  {isInternalUser && (
                    <>
                      <td className="text-muted">—</td>
                      <td className="text-muted">—</td>
                    </>
                  )}
                  <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        step="any"
                        value={newPvp}
                        onChange={(e) => setNewPvp(e.target.value)}
                        placeholder="PVP"
                      />
                    </td>
                  <td className="text-center">
                    <Button variant="outline-success" size="sm" onClick={addItem}>
                      Agregar
                    </Button>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>

      {/* OPPORTUNITY LOG */}
      <Row className="mt-4 text-start">
        <Col>
          <Form.Label>Opportunity Log:</Form.Label>
            <div className="p-3 border bg-light rounded">
      <Row className="g-4">
        {/* === COLUMNA IZQUIERDA: ÁREA DE TEXTO (Sin cambios) === */}
        <Col md={7}>
          {isInternalUser && isTask && (
            <Form.Group controlId="taskTitle" className="mb-3">
              <Form.Label className="fw-bold">Título de la tarea</Form.Label>
              <Form.Control
                type="text"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                placeholder="Nombre corto de la tarea"
                required
              />
              <Form.Text className="text-muted">
                Obligatorio si marcas «Es una tarea». Se usa como nombre de la tarea en Talent.
              </Form.Text>
            </Form.Group>
          )}
          <Form.Group controlId="logEntryText">
            <Form.Label className="fw-bold">
              {isInternalUser && isTask ? 'Descripción de la tarea' : 'Detalle de la Entrada'}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              placeholder={
                isInternalUser && isTask
                  ? 'Describe la tarea (se guardará como descripción en Talent y en el log de la oportunidad)...'
                  : 'Añade una entrada detallada al log...'
              }
              value={logEntry}
              onChange={(e) => setLogEntry(e.target.value)}
            />
          </Form.Group>
        </Col>

        {/* === COLUMNA DERECHA: PANEL DE OPCIONES (Ajustado) === */}
        <Col md={5}>
            <Form.Label className="fw-bold">Opciones</Form.Label>
            
            {/* --- INICIO DEL CAMBIO: Fila para las opciones principales --- */}
            <Row className="mt-2">
                <Col>
                    <Form.Check 
                        type="switch"
                        id="public-switch"
                        label="Privada"
                        checked={isPrivate}
                        onChange={(e) => setIsPrivate(e.target.checked)}
                    />
                </Col>
                {isInternalUser && (
                <Col>
                    <Form.Check 
                        type="checkbox"
                        id="task-checkbox"
                        label="Es una tarea"
                        checked={isTask}
                        onChange={(e) => handleTaskToggle(e.target.checked)}
                    />
                </Col>
                )}
            </Row>
            {/* --- FIN DEL CAMBIO --- */}
            
            {/* La fecha aparece debajo si la tarea está marcada */}
            {isInternalUser && isTask && (
                <>
                  <Form.Group controlId="taskAssignee" className="mt-3">
                    <Form.Label>Responsable</Form.Label>
                    <Button
                      variant="outline-secondary"
                      className="w-100 d-flex align-items-center justify-content-between"
                      onClick={() => setAssigneePickerOpen(true)}
                    >
                      <span className="d-inline-flex align-items-center gap-2 text-truncate">
                        <UserAvatar
                          usr_id={selectedAssignee ? selectedAssignee.usr_id : null}
                          usr_name={selectedAssignee ? selectedAssignee.displayName : 'Sin responsable'}
                        />
                        <span className="text-truncate">
                          {selectedAssignee ? selectedAssignee.displayName : 'Seleccionar responsable'}
                        </span>
                      </span>
                      <span aria-hidden="true">&rsaquo;</span>
                    </Button>
                  </Form.Group>
                  <Form.Group controlId="expiryDate" className="mt-3">
                      <Form.Label>Fecha de vencimiento</Form.Label>
                      <Form.Control 
                          type="date"
                          value={expiryDate}
                          onChange={(e) => setExpiryDate(e.target.value)}
                      />
                  </Form.Group>
                </>
            )}
        </Col>
      </Row>

      {/* === BOTÓN DE ENVÍO FINAL (Sin cambios) === */}
      <div className="d-grid mt-4">
        <Button
          variant="primary"
          size="lg"
          onClick={sendLog}
          disabled={isInternalUser && isTask && !taskTitle.trim()}
        >
          Guardar Entrada
        </Button>
      </div>
    </div>
          <div className="log-box text-left"> {/* Aseguramos que el contenedor principal alinee su texto a la izquierda */}
            {log.length === 0 ? (
              <p className="text-gray-500 italic">No hay entradas en el registro aún.</p>
            ) : (
              log.map((l) => {
                    const expiryDate2 = new Date(l.expiryDate);
                    const today = new Date();

                    // Normalizamos ambas fechas a medianoche para comparar solo el día
                    expiryDate2.setHours(0, 0, 0, 0);
                    today.setHours(0, 0, 0, 0);

                    // Verificamos si la fecha ya pasó o es hoy
                    const isOverdueOrToday = expiryDate2 <= today;
                return(
                <div key={l.id}   className={`p-3 rounded mb-3 shadow-sm border ${parseInt(l.is_private) == 1 ? 'bg-success-subtle' : 'bg-light'}`}>
                  <div className="d-flex flex-column flex-sm-row align-items-sm-baseline mb-2 gap-3">
                    {/* Nombre del usuario */}
                    <Badge  
                      bg="warning"
                      pill
                      className="chip d-inline-flex align-items-center px-3 py-2 fs-8"
                    >
                      {l.user_by}
                    </Badge>

                    {/* Fecha */}
                    <span className="text-muted small">
                      {new Date(l.issue_date).toLocaleString()}
                    </span>

                    {/* Tipo */}
                    <Badge  
                      bg={ l.issue_type == 1 ? 'danger' :  l.issue_type == 2 ? 'info': l.issue_type == 3 ? 'secondary':'warning'}
                      pill
                      className="chip d-inline-flex align-items-center px-3 py-2 fs-8"
                    >
                      {l.type_name}
                    </Badge>
                   {l.issue_type == 3 && (
                      // Cambiamos justify-content-between por gap-3
                      <div className="d-flex align-items-center gap-3">

                        {!l.done && (
                        <span className={`small fw-bold ${isOverdueOrToday ? 'text-danger' : 'text-muted'}`}>
                          Due date: {new Date(l.expiryDate).toLocaleDateString()}
                        </span>
                        )}


                        {/* Contenedor para el checkbox (sin cambios) */}
                        <div className="d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input me-2"
                            id={`task-${l.id}`}
                            checked={l.done}
                            onChange={() => handleTaskCompletion(l.id, !l.done)}
                          />
                          <label htmlFor={`task-${l.id}`} className="form-check-label">
                            Done
                          </label>
                        </div>

                      </div>
                    )}
                  </div>

                  {/* Descripción */}
                  <p className="text-dark lh-sm text-start">{l.description}</p>
                </div>
              )})
            )}
          </div>
        </Col>
      </Row>
      </div>
    }
      <Modal_proyectos
        isOpen={assigneePickerOpen}
        onClose={() => {
          setAssigneePickerOpen(false);
          setAssigneeSearch('');
        }}
        title="Asignar tarea a"
      >
        <div className="modal-inner">
          <div className="mb-3">
            <div className="input-group input-group-sm">
              <input
                type="search"
                className="form-control"
                placeholder="Buscar por nombre o correo..."
                value={assigneeSearch}
                onChange={(e) => setAssigneeSearch(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="list-group list-group-flush" style={{ maxHeight: 'min(55vh, 360px)', overflowY: 'auto' }}>
            {filteredAssigneeOptions.length === 0 ? (
              <p className="small text-muted mb-0 py-3 text-center">No hay miembros que coincidan.</p>
            ) : (
              filteredAssigneeOptions.map((opt) => (
                <button
                  key={opt.usr_id}
                  type="button"
                  className={`list-group-item list-group-item-action d-flex align-items-center gap-3 py-2 px-2${
                    opt.usr_id === newTaskAssignUsrId ? ' active' : ''
                  }`}
                  onClick={() => {
                    setNewTaskAssignUsrId(opt.usr_id);
                    setAssigneePickerOpen(false);
                    setAssigneeSearch('');
                  }}
                >
                  <UserAvatar usr_id={opt.usr_id} usr_name={opt.displayName} />
                  <span className="text-start text-truncate flex-grow-1">{opt.displayName}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </Modal_proyectos>
    </Container>
  );
}
